import { useCallback, useEffect, useState } from 'react'
import { TestingMethodType } from '@phrasee/phrasee-typings/Graphql/interfaces'
import { ResponseVariant } from 'workflow/Insights/Insights'
import { loadResultsDetails } from 'workflow/Workflow.actions'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import MainContent from 'features/unifiedFlow/components/MainContent'
import useTweakSuccess from 'features/unifiedFlow/contentPage/hooks/useTweakSuccess'
import useVariantTweakWebsocketSubscription from 'features/unifiedFlow/contentPage/hooks/useVariantTweakWebsocketSubscription'

import { getIsOptimized } from '../helpers'
import { useSelectedComponentElement } from '../hooks'
import InputResultModal from '../results/inputResult/InputResultModal'
import { VariantLanguageContextProvider } from '../review/context/VariantLanguageContext'
import useElementState from '../review/hooks/useElementState'
import LeftSection from '../review/leftSection'

import ManageTableWithFilters from './components/ManageTableWithFilters'
import { VariantStatus } from './components/VariantStatusFilters'
import RightSection from './rightSection/RightSection'

const Manage = () => {
  const dispatch = useAppDispatch()
  const campaignData = useAppSelector(
    (state) => state.campaignStates.campaignData
  )
  const broadcastTypes: TestingMethodType[] = [
    'split_test_partial',
    'split_test_full',
  ]
  const isBroadcastCampaign = broadcastTypes.includes(
    campaignData?.campaign_configuration?.testing_method
      ?.type as TestingMethodType
  )
  const [selectedPersonalizationId, setSelectedPersonalizationId] =
    useState<string | undefined>()
  const [variant, setVariant] = useState<ResponseVariant | undefined>()
  const [statusFilter, setStatusFilter] = useState<VariantStatus>('pending')
  const [hasLoadingVariants, setHasLoadingVariants] = useState(false)

  useEffect(() => {
    setStatusFilter(isBroadcastCampaign ? 'live' : 'pending')
  }, [isBroadcastCampaign])
  const [isInputResultModalOpen, setIsInputResultModalOpen] = useState(false)
  const { data: selectedElement } = useSelectedComponentElement()
  const isOptimized = getIsOptimized(selectedElement)
  useElementState()

  const handleSubmitInputResults = useCallback(() => {
    if (isOptimized && selectedElement?.campaign_id) {
      setIsInputResultModalOpen(false)
      dispatch(loadResultsDetails(selectedElement?.campaign_id))
    }
  }, [isOptimized, selectedElement?.campaign_id, dispatch])

  // Websocket subscription for tweak/reject operations
  const { tweakSuccessVariantIds, onTweakSuccess } = useTweakSuccess()

  useVariantTweakWebsocketSubscription({
    onTweakSuccess,
  })

  return (
    <VariantLanguageContextProvider>
      <MainContent className="flex-wrap" id="main-content-page">
        <LeftSection
          onSelectPersonalization={setSelectedPersonalizationId}
          selectedPersonalizationId={selectedPersonalizationId}
          statusFilter={statusFilter}
        />
        <div className="relative flex flex-1">
          <ManageTableWithFilters
            statusFilter={statusFilter}
            onSelectVariant={setVariant}
            tweakSuccessVariantIds={tweakSuccessVariantIds}
            selectedPersonalizationId={selectedPersonalizationId}
            onStatusFilterChange={setStatusFilter}
            onLoadingStateChange={setHasLoadingVariants}
          />
          <RightSection
            selectedVariant={variant}
            statusFilter={statusFilter}
            hasLoadingVariants={hasLoadingVariants}
            onInputResultsClick={() => setIsInputResultModalOpen(true)}
          />
        </div>
      </MainContent>
      {isOptimized && (
        <InputResultModal
          isOpen={isInputResultModalOpen}
          onClose={() => setIsInputResultModalOpen(false)}
          onSubmit={handleSubmitInputResults}
        />
      )}
    </VariantLanguageContextProvider>
  )
}

export default Manage
